import { supabase } from '../../../lib/supabase';
import { API_BASE } from '../../../lib/apiBase';

export async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  return {
    Authorization: `Bearer ${data.session?.access_token}`,
    'Content-Type': 'application/json',
  };
}

export async function apiFetch(path: string, opts?: RequestInit): Promise<unknown> {
  const h = await authHeaders();
  const r = await fetch(`${API_BASE}${path}`, { ...opts, headers: { ...h, ...opts?.headers } });
  if (!r.ok) throw new Error('Failed');
  return r.json();
}

export const EVENTS = [
  'signup',
  'email_confirmed',
  'profile_complete',
  'first_survey_start',
  'first_survey_complete',
  'first_redemption',
  'reactivation',
];

export const inputCls = 'bg-slate-700 text-white rounded-lg px-3 py-2 text-sm border border-slate-600';

// ── Date range presets ──────────────────────────────────────────────────────

export type RangePreset = '7d' | '30d' | 'mtd' | 'all' | 'custom';

export interface DateRange {
  from: Date | null;
  to: Date | null;
  preset: RangePreset;
}

function startOfUtcDay(d: Date): Date {
  const x = new Date(d);
  x.setUTCHours(0, 0, 0, 0);
  return x;
}

export function rangeFromPreset(preset: RangePreset): DateRange {
  const now = new Date();
  if (preset === 'all') return { from: null, to: null, preset };
  if (preset === 'mtd') {
    const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return { from, to: now, preset };
  }
  const days = preset === '7d' ? 7 : 30;
  const from = startOfUtcDay(new Date(now.getTime() - days * 24 * 60 * 60 * 1000));
  return { from, to: now, preset };
}

/** Build a `?from=&to=` query string for the API. Empty string when preset='all'. */
export function rangeToQuery(range: DateRange): string {
  if (!range.from && !range.to) return '';
  const parts: string[] = [];
  if (range.from) parts.push(`from=${encodeURIComponent(range.from.toISOString())}`);
  if (range.to) parts.push(`to=${encodeURIComponent(range.to.toISOString())}`);
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

// ── UI primitives ────────────────────────────────────────────────────────────

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`bg-slate-800 rounded-xl animate-pulse ${className}`} />;
}

export function EmptyState({ title, body }: { title: string; body?: string }) {
  return (
    <div className="bg-slate-800 rounded-xl p-10 text-center">
      <p className="text-sm text-white font-medium">{title}</p>
      {body && <p className="text-xs text-slate-400 mt-1">{body}</p>}
    </div>
  );
}

export function StatCard({ label, value, sub, tone = 'neutral' }: {
  label: string;
  value: string | number;
  sub?: string;
  tone?: 'neutral' | 'good' | 'bad' | 'warn' | 'info';
}) {
  const valueColor = tone === 'good' ? 'text-green-400'
    : tone === 'bad' ? 'text-red-400'
    : tone === 'warn' ? 'text-amber-400'
    : tone === 'info' ? 'text-blue-400'
    : 'text-white';
  return (
    <div className="bg-slate-800 rounded-xl p-4">
      <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
      <p className={`text-xl font-bold tabular-nums mt-1 ${valueColor}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

export function StatusPill({ active }: { active: boolean }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${active ? 'bg-green-900/50 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
      {active ? 'active' : 'off'}
    </span>
  );
}

export function DateRangePicker({ value, onChange }: { value: DateRange; onChange: (r: DateRange) => void }) {
  const presets: { key: RangePreset; label: string }[] = [
    { key: '7d', label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: 'mtd', label: 'This month' },
    { key: 'all', label: 'All time' },
    { key: 'custom', label: 'Custom' },
  ];

  const fromStr = value.from ? value.from.toISOString().slice(0, 10) : '';
  const toStr = value.to ? value.to.toISOString().slice(0, 10) : '';

  return (
    <div className="flex gap-2 items-center flex-wrap">
      <div className="flex gap-1 bg-slate-800 rounded-lg p-1">
        {presets.map((p) => (
          <button
            key={p.key}
            onClick={() => onChange(p.key === 'custom' ? { ...value, preset: 'custom' } : rangeFromPreset(p.key))}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${value.preset === p.key ? 'bg-primary text-white' : 'text-slate-400 hover:text-white'}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {value.preset === 'custom' && (
        <div className="flex gap-2 items-center">
          <input
            type="date"
            value={fromStr}
            onChange={(e) => onChange({ ...value, from: e.target.value ? new Date(e.target.value) : null, preset: 'custom' })}
            className={inputCls}
          />
          <span className="text-slate-500 text-xs">to</span>
          <input
            type="date"
            value={toStr}
            onChange={(e) => onChange({ ...value, to: e.target.value ? new Date(e.target.value) : null, preset: 'custom' })}
            className={inputCls}
          />
        </div>
      )}
    </div>
  );
}
